<template>
    <section
        v-if="visibleOverlays.length > 0"
        aria-label="Temporary swaps in place on this environment"
        class="border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 sm:px-6"
        data-testid="environment-overlay-banner"
    >
        <ul class="flex flex-col gap-2">
            <li
                v-for="overlay in visibleOverlays"
                :key="overlay.ulid"
                class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2"
                data-testid="environment-overlay"
            >
                <p class="min-w-0 flex-1">
                    <span class="font-medium">Environment swap in place.</span>
                    {{ environmentName }} is serving the targeting value and
                    rules from {{ sourceName(overlay) }}, until
                    <zen-date-time :value="overlay.expires_at" />.
                </p>

                <button
                    v-if="canRevert"
                    type="button"
                    class="shrink-0 font-medium underline underline-offset-2 hover:text-amber-950 focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-900 disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
                    :disabled="revertingUlids.includes(overlay.ulid)"
                    :aria-label="`Revert temporary swap on ${environmentName}`"
                    @click="revert(overlay)"
                >
                    Revert
                </button>
            </li>
        </ul>
    </section>
</template>

<script setup>
/**
 * Full-width notice under the top nav while a temporary swap (overlay) is
 * applying to the environment on screen (ZEN-765). Only the environment the
 * overlay targets shows it, and only while the swap's window is still running.
 * Users who can update the environment get a Revert button.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useSessionStore } from '@/stores/session'
import FlagSwapService from '@/services/flag-swap.service'
import { useFeatureFlag } from '@/utils/feature-flags'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { errorMessage, infoMessage } from '@/utils/notifications'
import { useSwapEndedEvents } from '@/composables/useSwapEndedEvents'

const props = defineProps({
    projectKey: {
        type: String,
        required: true,
    },
    environmentKey: {
        type: String,
        required: true,
    },
})

// How often the window is re-checked, so a swap disappears when it runs out without a reload.
const EXPIRY_CHECK_MS = 30 * 1000

const sessionStore = useSessionStore()
const { can } = usePermissions()

// Temporary swaps ship dark: nothing is requested or shown until the flag is on.
const { enabled: swapsEnabled } = useFeatureFlag(
    'enable-temporary-swaps',
    false
)

const project = computed(
    () =>
        sessionStore.data?.projects?.data?.find(
            (item) => item.key === props.projectKey
        ) ?? null
)

const environments = computed(() => project.value?.environments?.data ?? [])

const environment = computed(
    () =>
        environments.value.find((item) => item.key === props.environmentKey) ??
        null
)

const environmentName = computed(
    () => environment.value?.name ?? props.environmentKey
)

const canRevert = computed(() =>
    can(PERMISSIONS.ENVIRONMENT_UPDATE, {
        projectUlid: project.value?.ulid,
        environmentUlid: environment.value?.ulid,
    })
)

const overlays = ref([])
const now = ref(Date.now())
const revertingUlids = ref([])

// Only swaps whose window is still running are shown.
const visibleOverlays = computed(() =>
    overlays.value.filter(
        (overlay) => Date.parse(overlay.expires_at) > now.value
    )
)

// Incremented per load so a slow earlier response can't overwrite a newer one after switching environments.
let loadRequest = 0

const load = async () => {
    if (!swapsEnabled.value) {
        overlays.value = []
        return
    }

    const requestId = ++loadRequest
    try {
        const data = await FlagSwapService.list(
            props.projectKey,
            props.environmentKey
        )
        if (requestId === loadRequest) {
            overlays.value = Array.isArray(data?.data) ? data.data : []
        }
    } catch {
        // The notice is informational: if the list can't load, hide it rather than report an error on every screen.
        if (requestId === loadRequest) {
            overlays.value = []
        }
    }
}

const sourceName = (overlay) =>
    environments.value.find(
        (item) => item.ulid === overlay.source_environment_ulid
    )?.name ?? 'Another environment'

const revert = async (overlay) => {
    revertingUlids.value = [...revertingUlids.value, overlay.ulid]
    try {
        await FlagSwapService.revert(
            props.projectKey,
            props.environmentKey,
            overlay.ulid
        )
        infoMessage(
            'Swap reverted. This environment is serving its published values again.'
        )
    } catch (error) {
        // A 409 means the swap already ended or was reverted, so the reload below drops it from the notice.
        errorMessage(error)
    } finally {
        revertingUlids.value = revertingUlids.value.filter(
            (ulid) => ulid !== overlay.ulid
        )
        await load()
    }
}

let expiryTimer = null

onMounted(() => {
    expiryTimer = setInterval(() => {
        now.value = Date.now()
    }, EXPIRY_CHECK_MS)
})

onBeforeUnmount(() => {
    clearInterval(expiryTimer)
})

// A swap on this environment was reverted or ran out: drop it from the notice now, not at the next expiry check.
useSwapEndedEvents((event) => {
    if (
        event &&
        (event.projectKey !== props.projectKey ||
            event.environmentKey !== props.environmentKey)
    ) {
        return
    }

    void load()
})

// Reload when the environment changes or the flag resolves, so the notice always describes the screen on show.
watch(
    () => [props.projectKey, props.environmentKey, swapsEnabled.value],
    () => {
        now.value = Date.now()
        void load()
    },
    { immediate: true }
)
</script>
